import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Alert,
    TextField,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    CardActions,
    Chip,
    Avatar,
    Divider,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Add,
    Phone,
    FindInPage,
    Search,
    Clear,
    LocationOn,
    FileDownload,
    Edit,
    Delete,
    Pets as PetsIcon,
} from "@mui/icons-material";
import { MobilePaginationControls } from "../../components/common/MobilePaginationControls";
import { GridColDef } from "@mui/x-data-grid";
import { getCustomerFullName, getDefaultAddress } from "../../utils/customerUtils";
import { ServerSideDataTable } from "../../components/common/DataTable";
import { CustomerForm } from "../../components/forms/CustomerForm";
import { customerService } from "../../services/customerService";
import { Customer, Pet } from "../../types";
import { usePermissions } from "../../hooks/usePermissions";
import { useCustomerDetails } from "../../components/customers/useCustomerDetails";

export const CustomerList: React.FC = () => {
    const { canWrite } = usePermissions();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [formOpen, setFormOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    );
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    // The customer details dialog (shared with the All Orders page). A change
    // made from it (edit, default address, pets) updates that row in place,
    // keeping the current search and page.
    const customerDetails = useCustomerDetails({
        onCustomerUpdated: (updated) =>
            setCustomers((rows) =>
                rows.map((row) =>
                    row.customer_id === updated.customer_id ? { ...row, ...updated } : row,
                ),
            ),
    });

    // Pagination state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [totalRows, setTotalRows] = useState(0);

    // Search states — seeded from ?search= if we arrived via a deep link
    // (e.g. "Find in Customers" from a contact message), so the existing
    // debounced search effect below picks it up and runs automatically.
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState<string>(
        () => searchParams.get("search") || "",
    );
    const abortControllerRef = useRef<AbortController | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Strip the param once consumed so it doesn't linger in the URL bar
    // or re-trigger on a later param change.
    useEffect(() => {
        if (searchParams.get("search")) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [exporting, setExporting] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const mobileTotalPages = Math.max(
        1,
        Math.ceil(totalRows / paginationModel.pageSize),
    );
    const mobileStartIndex = paginationModel.page * paginationModel.pageSize;
    const mobileEndIndex = Math.min(
        mobileStartIndex + customers.length,
        totalRows,
    );

    // Constants
    const MIN_SEARCH_LENGTH = 1;
    const DEBOUNCE_DELAY = 800;

    const fetchCustomers = useCallback(
        async (search?: string) => {
            try {
                setLoading(true);
                const response = await customerService.getCustomers({
                    skip: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                    active_only: true,
                    search: search || undefined,
                    sort_by: "create_time",
                    sort_order: "desc",
                });
                setCustomers(response.customers);
                setTotalRows(response.total);
            } catch (err: any) {
                setError("Failed to load customers");
                console.error("Customers fetch error:", err);
            } finally {
                setLoading(false);
            }
        },
        [paginationModel],
    );

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers, paginationModel]);

    const performSearch = useCallback(
        async (query: string) => {
            // Cancel previous request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller
            abortControllerRef.current = new AbortController();

            try {
                setLoading(true);

                const response = await customerService.getCustomers({
                    skip: paginationModel.page * paginationModel.pageSize,
                    limit: paginationModel.pageSize,
                    active_only: true,
                    search: query,
                    sort_by: "create_time",
                    sort_order: "desc",
                });

                setCustomers(response.customers);
                setTotalRows(response.total);

                // Focus search input after results load for better UX
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 100);
            } catch (err: any) {
                if (err.name !== "AbortError") {
                    setError("Failed to search customers");
                    console.error("Customers search error:", err);
                }
            } finally {
                setLoading(false);
            }
        },
        [paginationModel],
    );

    // Debounced search effect
    useEffect(() => {
        if (searchTerm.length >= MIN_SEARCH_LENGTH) {
            const timer = setTimeout(() => {
                performSearch(searchTerm);
            }, DEBOUNCE_DELAY);

            return () => clearTimeout(timer);
        } else if (searchTerm.length === 0) {
            // Clear search results when search is empty
            fetchCustomers();
        }
    }, [searchTerm, performSearch, fetchCustomers]);

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchTerm(value);

            // If user clears search, immediately fetch all customers
            if (value.length === 0) {
                fetchCustomers();
            }
        },
        [fetchCustomers],
    );

    const handleSearchKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (
                event.key === "Enter" &&
                searchTerm.length >= MIN_SEARCH_LENGTH
            ) {
                event.preventDefault();
                performSearch(searchTerm);
            }
        },
        [searchTerm, performSearch],
    );

    const handleClearSearch = useCallback(() => {
        setSearchTerm("");
        setPaginationModel({ page: 0, pageSize: 50 }); // Reset to first page
        fetchCustomers();

        // Focus search input after clearing
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 100);
    }, [fetchCustomers]);

    const handlePaginationModelChange = (model: {
        page: number;
        pageSize: number;
    }) => {
        setPaginationModel(model);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await customerService.exportCustomers({
                search: searchTerm || undefined,
                active_only: true,
                sort_by: "create_time",
                sort_order: "desc",
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            const dateStr = new Date().toISOString().split("T")[0];
            link.setAttribute("download", `customers_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error("Failed to export customers:", error);
            setError("Failed to export customers");
        } finally {
            setExporting(false);
        }
    };

    const handleAdd = () => {
        setSelectedCustomer(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = async (id: string) => {
        const customer = customers.find((c: Customer) => c.customer_id === id);
        if (customer) {
            // Always fetch fresh addresses for editing to ensure we have complete data including map_link
            try {
                const addresses =
                    await customerService.getCustomerAddresses(id);
                console.log("Fetched addresses for editing:", addresses);
                // Create a new customer object with addresses for editing
                const customerWithAddresses = { ...customer, addresses };
                setSelectedCustomer(customerWithAddresses);
            } catch (err) {
                console.error(
                    "Failed to fetch customer addresses for editing:",
                    err,
                );
                // Still allow editing even if addresses fail to load
                setSelectedCustomer(customer);
            }
            setFormMode("edit");
            setFormOpen(true);
        }
    };

    const handleView = (id: string) => {
        const customer = customers.find((c: Customer) => c.customer_id === id);
        // Loads fresh addresses/pets before opening.
        if (customer) customerDetails.showCustomer(customer);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this customer?")) {
            try {
                await customerService.deleteCustomer(id);
                await fetchCustomers(); // Refresh the list
            } catch (err) {
                setError("Failed to delete customer");
            }
        }
    };

    const handleFormSubmit = async (customer: Customer) => {
        await fetchCustomers(); // Refresh the list
        setFormOpen(false);
    };

    const columns: GridColDef[] = [
        {
            field: "full_name",
            headerName: "Name",
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => getCustomerFullName(row),
        },
        {
            field: "address",
            headerName: "Address",
            flex: 1.2,
            minWidth: 220,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const address = getDefaultAddress(
                    (params.row as Customer).addresses,
                );
                if (!address) return "-";
                const line = `${address.street_address}, ${address.city}`;
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocationOn fontSize="small" color="action" />
                        {address.map_link ? (
                            <a
                                href={address.map_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1976d2", textDecoration: "none" }}
                            >
                                {line}
                            </a>
                        ) : (
                            <span>{line}</span>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "pets",
            headerName: "Pets",
            flex: 1,
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const names = ((params.row as Customer).pets || [])
                    .map((p: Pet) => p.name)
                    .join(", ");
                if (!names) return "-";
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PetsIcon fontSize="small" color="action" />
                        {names}
                    </Box>
                );
            },
        },
        {
            field: "phone",
            headerName: "Phone",
            width: 150,
            renderCell: (params) =>
                params.value ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Phone fontSize="small" color="action" />
                        {params.value}
                    </Box>
                ) : (
                    "-"
                ),
        },
        {
            field: "create_time",
            headerName: "Registered",
            width: 120,
            type: "date",
            valueGetter: (value: string) => new Date(value),
        },
    ];

    const rows = customers.map((customer: Customer) => ({
        id: customer.customer_id,
        ...customer,
    }));

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Customers ({totalRows})
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Search Input */}
                    <TextField
                        inputRef={searchInputRef}
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        size="small"
                        sx={{
                            minWidth: { xs: 0, sm: 250 },
                            width: { xs: "100%", sm: "auto" },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearSearch}
                                    >
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        onClick={handleExport}
                        disabled={exporting}
                        color="success"
                    >
                        {exporting
                            ? "Exporting..."
                            : isMobile
                              ? "CSV"
                              : "Export CSV"}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FindInPage />}
                        onClick={() =>
                            window.open("/customers/duplicates", "_blank")
                        }
                    >
                        {isMobile ? "Duplicates" : "Manage Duplicates"}
                    </Button>
                    {canWrite && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Add />}
                            onClick={handleAdd}
                        >
                            {isMobile ? "Add" : "Add Customer"}
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : customers.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No customers found.
                            </Typography>
                        </Box>
                    ) : (
                        customers.map((customer: Customer) => (
                            <Card
                                key={customer.customer_id}
                                variant="outlined"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ pb: "8px !important" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1.5,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                flexShrink: 0,
                                                bgcolor: "primary.light",
                                                color: "primary.dark",
                                                fontWeight: "bold",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            {customer.first_name?.[0]?.toUpperCase()}
                                            {customer.last_name?.[0]?.toUpperCase()}
                                        </Avatar>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "flex-start",
                                                    mb: 0.25,
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="bold"
                                                    sx={{
                                                        flex: 1,
                                                        mr: 1,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {getCustomerFullName(
                                                        customer,
                                                    )}
                                                </Typography>
                                                <Chip
                                                    label={customer.status}
                                                    color={
                                                        customer.status ===
                                                        "ACTIVE"
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    size="small"
                                                    sx={{
                                                        flexShrink: 0,
                                                        height: 20,
                                                        fontSize: "0.65rem",
                                                    }}
                                                />
                                            </Box>

                                            {(() => {
                                                const address =
                                                    getDefaultAddress(
                                                        customer.addresses,
                                                    );
                                                if (!address) return null;
                                                const line = `${address.street_address}, ${address.city}`;
                                                return (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 0.5,
                                                            mb: 0.25,
                                                        }}
                                                    >
                                                        <LocationOn
                                                            sx={{
                                                                fontSize: 12,
                                                                color: "text.secondary",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        {address.map_link ? (
                                                            <Typography
                                                                variant="caption"
                                                                component="a"
                                                                href={
                                                                    address.map_link
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: "primary.main",
                                                                    overflow:
                                                                        "hidden",
                                                                    textOverflow:
                                                                        "ellipsis",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {line}
                                                            </Typography>
                                                        ) : (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    overflow:
                                                                        "hidden",
                                                                    textOverflow:
                                                                        "ellipsis",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {line}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })()}

                                            {customer.pets &&
                                                customer.pets.length > 0 && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 0.5,
                                                            mb: 0.25,
                                                        }}
                                                    >
                                                        <PetsIcon
                                                            sx={{
                                                                fontSize: 12,
                                                                color: "text.secondary",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {customer.pets
                                                                .map(
                                                                    (p) =>
                                                                        p.name,
                                                                )
                                                                .join(", ")}
                                                        </Typography>
                                                    </Box>
                                                )}

                                            {customer.phone && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        mb: 0.25,
                                                    }}
                                                >
                                                    <Phone
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "text.secondary",
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {customer.phone}
                                                    </Typography>
                                                </Box>
                                            )}

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block" }}
                                            >
                                                Registered:{" "}
                                                {new Date(
                                                    customer.create_time,
                                                ).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>

                                <Divider />

                                <CardActions sx={{ px: 1.5, py: 1, gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<FindInPage />}
                                        onClick={() =>
                                            handleView(customer.customer_id)
                                        }
                                        sx={{ flex: 1 }}
                                    >
                                        View
                                    </Button>
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() =>
                                                handleEdit(customer.customer_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={() =>
                                                handleDelete(customer.customer_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        ))
                    )}
                    <MobilePaginationControls
                        page={paginationModel.page}
                        totalPages={mobileTotalPages}
                        canGoNext={paginationModel.page < mobileTotalPages - 1}
                        canGoPrev={paginationModel.page > 0}
                        onNext={() =>
                            handlePaginationModelChange({
                                page: paginationModel.page + 1,
                                pageSize: paginationModel.pageSize,
                            })
                        }
                        onPrev={() =>
                            handlePaginationModelChange({
                                page: paginationModel.page - 1,
                                pageSize: paginationModel.pageSize,
                            })
                        }
                        startIndex={mobileStartIndex}
                        endIndex={mobileEndIndex}
                        total={totalRows}
                    />
                </Box>
            ) : (
                <ServerSideDataTable
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    onEdit={canWrite ? handleEdit : undefined}
                    onDelete={canWrite ? handleDelete : undefined}
                    onView={handleView}
                    totalRows={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSize={paginationModel.pageSize}
                />
            )}

            <CustomerForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
                customer={selectedCustomer}
                mode={formMode}
            />

            {customerDetails.dialog}
        </Box>
    );
};
